import { defineStore } from 'pinia'
import { reactive } from 'vue'
import cloneDeep from 'lodash/cloneDeep'

type Nullable<T> = T | null

export interface IAdminAuthStoreState {
	user: Nullable<IUserModel>
}

export interface IAdminAuthStore {
	readonly state: {
		user: Nullable<IUserModel>
	}
	setUser: (user: Nullable<IUserModel>) => void
	login: (login: string, password: string) => Promise<IUserModel>
	logout: () => void
	fetchUser: () => Promise<IUserModel>
	getUser: ComputedRef<IUserModel | null>
	isLoggedIn: ComputedRef<boolean>
	isAdmin: ComputedRef<boolean>
	isCmsEditor: ComputedRef<boolean>
	getPermission: (permission: EUserPermissions | EUserPermissions[]) => boolean
}

export const useAdminAuthStore = defineStore('adminAuthStore', (): IAdminAuthStore => {
	const _state = reactive<IAdminAuthStoreState>({
		user: null,
	})

	const setUser = (user: Nullable<IUserModel>) => {
		_state.user = cloneDeep(user)
	}

	const fetchUser = async () => {
		const user = await ApiService.adminFetchUser()
		setUser(user)
		return user
	}

	const login = async (login: string, password: string) => {
		await ApiService.adminLogin(login, password)

		const user = await fetchUser()
		const cookie = useCookie('is_logged_admin')
		cookie.value = 'true'
		if (!user) logout()
		return user
	}

	const logout = async () => {
		const cookie = useCookie('is_logged_admin')
		_state.user = null
		cookie.value = 'false'

		await ApiService.adminLogout()
	}

	const getUser = computed(() => {
		return _state.user
	})

	const isLoggedIn = computed(() => {
		return !!_state.user
	})

	const isAdmin = computed(() => {
		if (!_state.user) return false
		return _state.user.permissions.includes(EUserPermissions.ADMIN)
	})

	const getPermission = (permission: EUserPermissions | EUserPermissions[]) => {
		if (Array.isArray(permission)) {
			return permission.some((perm) => _state.user?.permissions.includes(perm))
		}
		return _state.user?.permissions.includes(permission)
	}

	const isCmsEditor = computed(() => getPermission([EUserPermissions.CMS_PL, EUserPermissions.CMS_EN]))

	return {
		state: _state,
		setUser,
		login,
		logout,
		fetchUser,
		getUser,
		isLoggedIn,
		isAdmin,
		isCmsEditor,
		getPermission,
	}
})
